import { useCallback, useEffect, useState } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useCan, useCompanies, useLogout, useMe, useUnreadCount } from '../api/hooks';
import { CommandPalette } from '../components/CommandPalette';
import { I } from '../components/Icons';
import { OfflineBanner } from '../components/OfflineBanner';
import { Btn } from '../components/ui';
import { type I18nKey, useI18n } from '../i18n';
import { useCompanyFilter } from '../state/company';
import { useTopbarState } from '../state/topbar';
import { CompanyChip, CompanySwitcher } from './CompanySwitcher';
import { useShortcuts } from './useShortcuts';

const TABS: { key: string; path: string; label: I18nKey | ''; icon: React.ReactNode }[] = [
  { key: 'home', path: '/', label: 'nav.home', icon: I.home },
  { key: 'docs', path: '/doklady', label: 'nav.documents', icon: I.docs },
  { key: 'new', path: '/novy', label: '', icon: I.plus },
  { key: 'payments', path: '/platby', label: 'nav.payments', icon: I.pay },
  { key: 'stock', path: '/sklad', label: 'nav.stock', icon: I.box },
  { key: 'wholesale', path: '/velkoobchod', label: 'nav.wholesale', icon: I.transfer },
  { key: 'more', path: '/vice', label: 'nav.more', icon: I.more },
];

/** Položky sidebaru na desktopu; stejné cíle jako tab bar a obrazovka Více. */
const SIDEBAR: {
  path: string;
  label: I18nKey;
  icon: React.ReactNode;
  add?: boolean;
  sep?: boolean;
  /** svítí jen na přesné cestě (Více má pod sebou všechny /vice/…) */
  exact?: boolean;
}[] = [
  { path: '/', label: 'nav.home', icon: I.home },
  { path: '/asistent', label: 'nav.assistant', icon: I.sparkle },
  { path: '/doklady', label: 'nav.documents', icon: I.docs },
  { path: '/novy', label: 'nav.new', icon: I.plus, add: true },
  { path: '/platby', label: 'nav.payments', icon: I.pay },
  { path: '/zakazky', label: 'nav.jobs', icon: I.wrench },
  { path: '/kalendar', label: 'nav.calendar', icon: I.calendar },
  { path: '/ukoly', label: 'nav.tasks', icon: I.repeat },
  { path: '/sklad', label: 'nav.stock', icon: I.box },
  { path: '/velkoobchod', label: 'nav.wholesale', icon: I.transfer },
  { path: '/prehledy', label: 'nav.reports', icon: I.chart },
  { path: '/weby', label: 'nav.web', icon: I.globe },
  { path: '/reklamy', label: 'nav.ads', icon: I.megaphone },
  { path: '/vice/kontakty', label: 'nav.contacts', icon: I.people, sep: true },
  { path: '/vice/ucetni', label: 'nav.accountant', icon: I.export },
  { path: '/vice/firmy', label: 'nav.companies', icon: I.building },
  { path: '/vice/rady', label: 'nav.series', icon: I.hash },
  { path: '/vice/pohoda', label: 'nav.pohoda', icon: I.export },
  { path: '/vice', label: 'nav.more', icon: I.more, exact: true },
  { path: '/vice/navody', label: 'nav.guides', icon: I.help },
];

function activeTab(pathname: string): string {
  if (pathname === '/') return 'home';
  const seg = `/${pathname.split('/')[1]}`;
  return TABS.find((t) => t.path === seg)?.key ?? 'more';
}

function sidebarActive(pathname: string, path: string, exact?: boolean): boolean {
  if (path === '/' || exact) return pathname === path;
  return pathname === path || pathname.startsWith(`${path}/`);
}

/**
 * Rámec aplikace: do 900 px topbar + obrazovka + spodní tab bar, od 900 px sidebar + obsah.
 * Obojí je v DOM; o zobrazení rozhoduje CSS (layout.css). Vyžaduje přihlášení.
 */
export function Shell() {
  const { data: me, isPending } = useMe();
  const { data: companies, isPending: companiesPending } = useCompanies();
  const { companyId, isAll, resolving, resolveDefault } = useCompanyFilter();
  const location = useLocation();
  const navigate = useNavigate();
  const topbar = useTopbarState();
  const logout = useLogout();
  const access = useCan(companyId && companyId !== 'all' ? companyId : null);
  // dokud /me nedorazí, tlačítko nechat; server 403 stejně vrátí
  const canIssue = !access.loaded || access.can('documents:issue');
  const [palette, setPalette] = useState(false);
  const { data: unread } = useUnreadCount();
  const { t, locale, setLocale } = useI18n();
  useEffect(() => {
    const l = me?.locale;
    if ((l === 'cs' || l === 'en') && l !== locale) setLocale(l);
  }, [me?.locale, locale, setLocale]);
  const unreadCount = unread?.unread ?? 0;
  const [help, setHelp] = useState(false);
  const openPalette = useCallback(() => setPalette(true), []);
  const toggleHelp = useCallback(() => setHelp((v) => !v), []);
  useShortcuts({ openPalette, toggleHelp });

  // jakmile dorazí firmy, zahodí se volba ukazující na smazanou firmu
  useEffect(() => {
    if (companies) resolveDefault(companies.map((s) => s.id));
  }, [companies, resolveDefault]);

  // po každé změně obrazovky nahoru, jako v prototypu
  // biome-ignore lint/correctness/useExhaustiveDependencies: záměrně reaguje na změnu cesty
  useEffect(() => {
    const el = document.getElementById('screen');
    if (el) el.scrollTop = 0;
  }, [location.pathname]);

  if (isPending) return <div className="phone" />;
  if (!me) return <Navigate to="/prihlaseni" replace />;
  // obrazovky se vykreslí až s ověřenou volbou firmy, ať se nikde nešáhne na neplatné id
  if (companiesPending || (companies && resolving)) return <div className="phone" />;
  // bez zvolené firmy se do aplikace nevstoupí – firmy jsou oddělené
  if (!companyId) return <Navigate to="/firmy" replace />;

  const active = activeTab(location.pathname);
  const goBack = () => {
    const idx = (window.history.state as { idx?: number } | null)?.idx ?? 0;
    if (idx > 0) navigate(-1);
    else navigate(topbar.back ?? '/');
  };

  return (
    <div className="phone">
      <aside id="sidebar" aria-label="Navigace">
        <div className="sb-brand">
          <div className="sb-app">iSupply Faktury</div>
          <CompanySwitcher />
        </div>
        <button type="button" className="sb-search" onClick={openPalette}>
          {I.search}
          <span>{t('nav.search')}</span>
          <kbd>Ctrl K</kbd>
        </button>
        {SIDEBAR.map((item) => (
          <div key={item.path}>
            {item.sep && <div className="sb-sep" />}
            {item.add ? (
              <div className="sb-add">
                <Btn
                  variant="primary"
                  full
                  disabled={isAll || !canIssue}
                  title={
                    isAll
                      ? 'Nejdřív vyber konkrétní firmu'
                      : canIssue
                        ? undefined
                        : 'Nemáš oprávnění vystavovat doklady'
                  }
                  onClick={() => navigate(item.path)}
                >
                  {item.icon}
                  {t(item.label)}
                </Btn>
              </div>
            ) : (
              <button
                type="button"
                className={`sb-item ${sidebarActive(location.pathname, item.path, item.exact) ? 'on' : ''}`.trim()}
                onClick={() => navigate(item.path)}
              >
                {item.icon}
                {t(item.label)}
              </button>
            )}
          </div>
        ))}
        <div className="sb-foot">
          <button type="button" className="link" onClick={() => navigate('/vice/notifikace')}>
            {t('nav.notifications')}
            {unreadCount ? ` (${unreadCount})` : ''}
          </button>
          <button type="button" className="link" onClick={() => navigate('/vice/ucet')}>
            {me.email}
          </button>
          <button
            type="button"
            className="link"
            onClick={() => logout.mutate(undefined, { onSuccess: () => navigate('/prihlaseni') })}
          >
            {t('nav.logout')}
          </button>
        </div>
      </aside>
      <header id="topbar">
        {topbar.back && (
          <button type="button" className="iconbtn" onClick={goBack} aria-label="Zpět">
            {I.back}
          </button>
        )}
        <h1>{topbar.title}</h1>
        {topbar.right === 'companyChip' && (
          <span className="only-m">
            <CompanyChip />
          </span>
        )}
        {location.pathname !== '/asistent' && (
          <button
            type="button"
            className="iconbtn ai"
            onClick={() => navigate('/asistent', { state: { from: location.pathname } })}
            aria-label="Zeptat se AI asistenta"
            title="AI asistent"
          >
            {I.sparkle}
          </button>
        )}
        <button
          type="button"
          className="iconbtn bell"
          onClick={() => navigate('/vice/notifikace')}
          aria-label={
            unreadCount
              ? `${t('nav.notifications')} (${t('plural.unread', { n: unreadCount })})`
              : t('nav.notifications')
          }
        >
          {I.bell}
          {unreadCount > 0 && <span className="cnt">{unreadCount > 99 ? '99+' : unreadCount}</span>}
        </button>
        <button
          type="button"
          className="iconbtn"
          onClick={openPalette}
          aria-label="Hledat (Ctrl+K)"
        >
          {I.search}
        </button>
      </header>
      <CommandPalette open={palette} onClose={() => setPalette(false)} />
      {help && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: zavírá se i klávesou ? a Esc
        <div className="palette-bg" onClick={() => setHelp(false)}>
          <div className="palette pad" role="dialog" aria-label={t('nav.shortcuts')}>
            <h3>{t('nav.shortcuts')}</h3>
            <div className="keys">
              <div>
                <kbd>Ctrl K</kbd> / <kbd>/</kbd> {t('nav.shortcuts.search')}
              </div>
              <div>
                <kbd>n</kbd> {t('nav.shortcuts.new')}
              </div>
              <div>
                <kbd>g</kbd> <kbd>h</kbd> přehled · <kbd>g</kbd> <kbd>d</kbd> doklady · <kbd>g</kbd>{' '}
                <kbd>p</kbd> platby
              </div>
              <div>
                <kbd>g</kbd> <kbd>s</kbd> sklad · <kbd>g</kbd> <kbd>r</kbd> přehledy · <kbd>g</kbd>{' '}
                <kbd>u</kbd> účetní
              </div>
              <div>
                <kbd>g</kbd> <kbd>k</kbd> kontakty · <kbd>g</kbd> <kbd>z</kbd> zakázky ·{' '}
                <kbd>g</kbd> <kbd>w</kbd> weby · <kbd>g</kbd> <kbd>a</kbd> asistent · <kbd>g</kbd>{' '}
                <kbd>c</kbd> kalendář
              </div>
              <div>
                <kbd>g</kbd> <kbd>n</kbd> nastavení · <kbd>g</kbd> <kbd>v</kbd> návody ·{' '}
                <kbd>?</kbd> tahle nápověda
              </div>
            </div>
          </div>
        </div>
      )}
      <main id="screen">
        <OfflineBanner />
        {companies?.find((c) => c.id === companyId)?.isTest && (
          <div className="test-banner" role="note">
            Testovací účetnictví – doklady mají vodoznak a e-maily jdou jen tobě.
          </div>
        )}
        <Outlet />
      </main>
      <nav id="tabbar">
        {TABS.map((tab) => {
          const add = tab.key === 'new';
          return (
            <button
              type="button"
              key={tab.key}
              className={`tab ${active === tab.key ? 'on' : ''} ${add ? 'add' : ''}`.trim()}
              disabled={add && (isAll || !canIssue)}
              onClick={() => navigate(tab.path)}
              aria-label={tab.label ? t(tab.label) : t('nav.new')}
            >
              {add ? <span className="circle">{tab.icon}</span> : tab.icon}
              {tab.label && <span>{t(tab.label)}</span>}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
